import { mapFromKey } from 'constants/ggVars';
import { PROJECT_SUMMARY_WITH_REPORT } from 'constants/routePaths';

import * as React from 'react';
import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import ProjectByIdQuery from '@graphql/queries/ProjectByIdQuery';
import ReportGgVarTuningPlot from '@graphql/queries/ReportGgVarTuningPlot';
import ReportProductsByReportIdQuery, {
  ReportProductsByReportIdResponse,
} from '@graphql/queries/ReportProductsByReportIdQuery';
import TextureDecompByReportIdAndProductIDQuery from '@graphql/queries/TextureDecompByReportIdAndProductIDQuery';
import { KeyboardBackspace as KeyboardBackspaceIcon } from '@mui/icons-material';
import { IconButton, Grid } from '@mui/material';
import { ProductVersion } from 'components/Report/ProductVersion';
import { getProductName } from 'components/Report/utils';
import { QaPreviewReportProvider } from 'hooks/QaPreviewReportContext';
import useCustomProductNames from 'hooks/useCustomProductNames';
import useMinMaxPq from 'hooks/useMixMaxPq';
import usePqTransform from 'hooks/usePqTransform';
import useQaPreviewSession from 'hooks/useQaPreviewSession';
import useReportSummary from 'hooks/useReportSummary';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RouterProps } from 'react-router';
import formatPath from 'utils/formatPath';
import { getQaPreviewSession } from 'utils/qaPreviewSession';

import LoadingScreen from '../../../components/LoadingScreen';
import TextureSummaryChartGroup from '../../../components/Report/Texture/TextureIntensityCharts/TextureSummaryChartGroup';
import selectWorkspaceProducerId from '../../../selectors/workspaceProducerId';
import { ProjectTabs } from '../../../services/customerPreferences';
import { PageHeader } from '../../../styles/themeComponents';
import { checkIsWrongWorkspace } from '../../../utils/afsUtils';
import styles from '../ProjectSummary.module.css';

// Clean the version param, as it might be the null string
export const cleanVersionParam = (versionParam: string): string => {
  if (!!versionParam && versionParam != 'null') {
    return decodeURI(versionParam);
  } else {
    return null;
  }
};

interface Props extends RouterProps {
  match: Record<string, any>;
  location: Record<string, any>;
}

const ProjectTextureSummaryChart: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const pqTransform = usePqTransform();

  const { match, history } = props;

  useEffect(() => {
    return () => {
      history.listen(() => {
        window.scrollTo(0, 0);
      });

      if (
        history.action === 'POP' &&
        reportId &&
        projectId !== undefined &&
        !getQaPreviewSession(reportId, projectId)
      ) {
        // Redirect to Project Summary page
        history.replace({
          pathname: formatPath(PROJECT_SUMMARY_WITH_REPORT, {
            projectId,
            reportId,
          }),
          state: {
            productId,
            version,
            tabIdx: ProjectTabs.TEXTURE_SUMMARY,
          },
        });
      }
    };
  }, []);

  const projectId: number | undefined = isNaN(parseInt(match.params.projectId))
    ? undefined
    : parseInt(match.params.projectId);
  const reportId: string = match.params.reportId;
  const productId: number = isNaN(parseInt(match.params.productId))
    ? null
    : parseInt(match.params.productId);
  const version: string = cleanVersionParam(match.params.version);

  const workspaceId = useSelector((state) => selectWorkspaceProducerId(state));

  // QA reviewers previewing an unapproved report bypass the project/workspace check
  const qaPreview = useQaPreviewSession(reportId, projectId);

  const { data: projectCheck, loading: projectCheckLoading } =
    useQuery<reports.ProjectByIdResponse>(ProjectByIdQuery, {
      variables: { projectId },
      skip: projectId === undefined || !!qaPreview,
    });

  const { data: reportProductsData, loading: reportProductsLoading } =
    useQuery<ReportProductsByReportIdResponse>(ReportProductsByReportIdQuery, {
      variables: { reportId },
      skip:
        !reportId ||
        (!qaPreview &&
          (projectCheckLoading ||
            checkIsWrongWorkspace(
              projectId,
              projectCheck,
              workspaceId,
              reportId,
            ))),
    });

  const isWrongWorkspace =
    reportProductsLoading ||
    (!qaPreview &&
      checkIsWrongWorkspace(
        projectId,
        projectCheck,
        workspaceId,
        reportId,
        productId,
        reportProductsData,
      ));

  const customProductNames = useCustomProductNames({
    projectId,
    skip: isWrongWorkspace || projectCheckLoading,
    previewSession: qaPreview,
  });
  const { productVersionPqs } = useReportSummary(reportId, {
    skip: isWrongWorkspace || projectCheckLoading,
  });

  const {
    data: minMaxPqData,
    loading: minMaxPqLoading,
    error: minMaxPqError,
  } = useMinMaxPq(reportId, isWrongWorkspace || projectCheckLoading);

  const {
    data: ggVarData,
    loading: ggVarLoading,
    error: ggVarError,
  } = useQuery<reports.GgVarTuningPlotResponse>(ReportGgVarTuningPlot, {
    variables: {
      reportID: reportId,
      productID: productId,
      ggVar: 'MOUTHFEEL',
    },
    skip: isWrongWorkspace || projectCheckLoading,
  });

  const {
    data: decompData,
    loading: decompLoading,
    error: decompError,
  } = useQuery<reports.ReportTextureDecompResponse>(
    TextureDecompByReportIdAndProductIDQuery,
    {
      variables: {
        reportID: reportId,
        productID: productId,
        version: version,
      },
      skip: isWrongWorkspace || projectCheckLoading,
    },
  );

  if (projectCheckLoading || reportProductsLoading) {
    return <LoadingScreen />;
  }

  if (isWrongWorkspace) {
    return (
      <div>
        <h2>{t('errors.accessForbidden', 'Access Forbidden')}</h2>
        <p>
          {t(
            'errors.noProjectAccess',
            'You do not have access to view this project. Please ensure you are in the correct workspace.',
          )}
        </p>
      </div>
    );
  }

  if (ggVarLoading || decompLoading || minMaxPqLoading) {
    return <LoadingScreen />;
  }

  function filterDecompData(
    data: reports.ReportTextureDecompResponse,
    version: string,
  ): reports.ReportTextureDecompRow[] {
    return data.allRpTextureDecomps.nodes.filter(
      (row) => row.version === version,
    );
  }

  function filterGgVarTuningData(
    data: reports.GgVarTuningPlotResponse,
    version: string,
  ): reports.GgVarTuningPlotRow[] {
    return data.allRpGgvarFlavorProfiles.nodes.filter(
      (row) => row.version === version,
    );
  }

  return (
    <QaPreviewReportProvider session={qaPreview}>
      <div>
        <div className={styles.tabContainer}>
          <Grid container>
            <Grid item xs={12}>
              <div className={styles.header}>
                <IconButton
                  onClick={history.goBack}
                  size="small"
                  style={{ marginLeft: -26 }}
                >
                  <KeyboardBackspaceIcon fontSize="small" />
                  <PageHeader>
                    {t('projects.textureFlavorSummary.back')}
                  </PageHeader>
                </IconButton>
              </div>
            </Grid>
            <Grid item xs={12}>
              <h4>
                {getProductName({
                  productNames: customProductNames,
                  productId: productId,
                  version,
                })}
              </h4>
            </Grid>
            <Grid container item xs={12} spacing={2}>
              <TextureSummaryChartGroup
                ggVar={mapFromKey('MOUTHFEEL')}
                pq={
                  productVersionPqs.find((i) =>
                    i.productVersion.equals(
                      new ProductVersion(productId, version),
                    ),
                  )?.pq
                }
                minPq={pqTransform.transform(minMaxPqData.minPq)}
                maxPq={pqTransform.transform(minMaxPqData.maxPq)}
                ggVarTuningData={filterGgVarTuningData(ggVarData, version)}
                decompData={filterDecompData(decompData, version)}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    </QaPreviewReportProvider>
  );
};

export default ProjectTextureSummaryChart;
